import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { AppCard, AppPage } from '../../components/general';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useAuth } from '@mezzodevelopment/mezzo-ui/auth';
import { WaterfallEditor } from './WaterfallEditor';
import { useWaterfalls } from '../../hooks/useWaterfalls';
import { IntegrationType, WaterfallVersionViewModel, WaterfallViewModel } from '../../models/waterfall';

export const WaterfallEditorContainer = () => {
  const { user, isLoading: authIsLoading } = useAuth();
  const navigate = useNavigate();
  const { waterfallId } = useParams<{ waterfallId?: string }>();
  const id = waterfallId ? Number(waterfallId) : undefined;

  const { waterfallDetailQuery, createWaterfall, updateWaterfall, createVersion, updateVersion, deleteVersion } =
    useWaterfalls(id);

  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);

  const isLoading = authIsLoading || !user || (id && waterfallDetailQuery.isLoading);

  const onCreate = async ({ name, integrationType }: any) => {
    const created = await createWaterfall({ name, integrationType });
    if (created.waterfallId) navigate(`/waterfalls/edit/${created.waterfallId}`);
  };

  const onUpdate = async (data: Partial<WaterfallViewModel>) => {
    if (!id || !waterfallDetailQuery.data) return;

    const existing = waterfallDetailQuery.data;

    // Only send fields that the API accepts
    const payload = {
      waterfallId: id,
      name: data.name ?? existing.name ?? '',
      integrationType: data.integrationType ?? existing.integrationType ?? IntegrationType.None,
      currentVersionId: existing.currentVersionId ?? null,
      disabled: existing.disabled ?? false,
      archived: existing.archived ?? false,
    };

    await updateWaterfall(payload);
    await waterfallDetailQuery.refetch();
  };

  const onCreateVersion = async (source: WaterfallVersionViewModel) => {
    if (!id) return;
    const newVersion = await createVersion({
      waterfallId: id,
      executionMode: source.executionMode,
      initialStep: source.initialStep,
    });
    await waterfallDetailQuery.refetch();
    if (newVersion?.waterfallVersionId) setSelectedVersionId(newVersion.waterfallVersionId);
  };

  const onSaveVersion = async (version: WaterfallVersionViewModel) => {
    if (!id) return;

    const payload = {
      waterfallVersionId: version.waterfallVersionId,
      waterfallId: id,
      executionMode: version.executionMode,
      initialStep: version.initialStep,
    };

    await updateVersion(payload);
    await waterfallDetailQuery.refetch();
  };

  const onSetActiveVersion = async (waterfallId: number, waterfallVersionId: number) => {
    await updateWaterfall({
      waterfallId,
      name: waterfallDetailQuery.data?.name ?? '',
      integrationType: waterfallDetailQuery.data?.integrationType ?? IntegrationType.None,
      currentVersionId: waterfallVersionId,
    });
    waterfallDetailQuery.refetch();
  };

  const onDeleteVersion = async (versionId: number) => {
    await deleteVersion(versionId);
    waterfallDetailQuery.refetch();
  };

  const onCancel = () => navigate('/waterfalls');

  if (isLoading) {
    return (
      <AppPage title="Waterfall Editor" breadcrumbs="Admin - Waterfalls">
        <AppCard>
          <div className="flex flex-column align-items-center justify-content-center gap-2">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" animationDuration=".5s" />
            <span>Loading Waterfall...</span>
          </div>
        </AppCard>
      </AppPage>
    );
  }

  return (
    <WaterfallEditor
      waterfall={waterfallDetailQuery.data ?? null}
      onSubmit={id ? onUpdate : onCreate}
      onCancel={onCancel}
      onCreateVersion={onCreateVersion}
      onSetActiveVersion={onSetActiveVersion}
      onSaveVersion={onSaveVersion}
      onDeleteVersion={onDeleteVersion}
      initialSelectedVersion={selectedVersionId}
    />
  );
};
